/* verqo — seller nav config (preview build · Feb 2026)
   Single source of truth for the seller-side mega-menu.
   Referenced by <PreviewNavHeader mode="seller" />.
   Iter Feb-B · Icons audited against seller portal (PortalShell.jsx).
     - Catalog     → LayoutGrid (portal /seller/catalog)
     - RFQ         → MessageSquare (portal /seller/rfq)
     - Credit terms→ Landmark (portal Payouts > Credit tab)
     - Buyer scores→ TrendingUp (portal /seller/buyers) — moved to Protect
     - Claims      → AlertTriangle (portal Orders > Claims)
     - Freight     → Truck (LTL/FTL is trucks, not air)
     - Insurance   → Shield (portal /seller/insurance)
   Payments description reworked to lead with "Same-day cash" / instant.
*/
import {
  LayoutGrid, Store, MessageSquare, Boxes,
  Banknote, Landmark, Receipt,
  Truck, AlertTriangle, TrendingUp, Shield,
  Building2, Warehouse,
  Sparkles, Shirt, UtensilsCrossed, Home, Heart, ShoppingBasket,
  BadgeDollarSign, Wallet, Briefcase, LineChart, Boxes as BoxesIcon,
} from "lucide-react";

const P = (slug) => `/product/${slug}`;
const S = (slug) => `/solutions/${slug}`;
const I = (slug) => `/solutions/industry/${slug}`;
const R = (slug) => `/solutions/role/${slug}`;
const C = (slug) => `/company/${slug}`;

export const SELLER_NAV = [
  {
    id: "product",
    label: "Product",
    mega: true,
    featured: [
      { slug: "banking", label: "Verqo Banking", tag: "Core", desc: "Replace your business bank — wallet, ACH & wires, plus virtual & physical cards backed by your balance.", href: P("payments"), icon: Landmark },
      { slug: "shipping-standalone", label: "Verqo Shipping", tag: "Standalone", desc: "Run all your sales channels' labels & freight from one hub — a cheaper ShipStation replacement, no B2B transactions required.", href: P("shipping"), icon: Truck },
    ],
    columns: [
      {
        heading: "Sell",
        items: [
          { label: "Catalog",    slug: "catalog",    href: P("catalog"),    desc: "One catalog, every buyer sees your price.",  icon: LayoutGrid },
          { label: "Storefront", slug: "storefront", href: P("storefront"), desc: "White-label B2B storefront in minutes.",      icon: Store },
          { label: "RFQ",        slug: "rfq",        href: P("rfq"),        desc: "Quote big orders without ping-pong emails.",  icon: MessageSquare },
          { label: "Dropship",   slug: "dropship",   href: P("dropship"),   desc: "Ship direct-to-consumer for your retailers.", icon: Boxes },
        ],
      },
      {
        heading: "Get paid",
        items: [
          { label: "Payments",         slug: "payments",         href: P("payments"),         desc: "Same-day cash on every invoice — every rail, one reconciliation.", icon: Banknote },
          { label: "Credit terms",     slug: "credit-terms",     href: P("credit-terms"),     desc: "Offer Net-30/60/90 to your buyers without carrying the risk.",       icon: Landmark },
          { label: "Seller financing", slug: "seller-financing", href: P("seller-financing"), desc: "Finance YOUR outstanding invoices — same-day advance on receivables.", icon: BadgeDollarSign },
          { label: "Auto-invoicing",   slug: "auto-invoicing",   href: P("auto-invoicing"),   desc: "Invoices generated the moment the truck rolls.",                     icon: Receipt },
        ],
      },
      {
        heading: "Ship",
        items: [
          { label: "Shipping", slug: "shipping", href: P("shipping"), desc: "Discounted rates from every major carrier.",       icon: Truck },
          { label: "Freight",  slug: "freight",  href: P("freight"),  desc: "LTL & FTL quotes side-by-side, one click to book.", icon: Truck },
          { label: "Claims",   slug: "claims",   href: P("claims"),   desc: "File and track carrier claims in one dashboard.",   icon: AlertTriangle },
        ],
      },
      {
        heading: "Protect",
        items: [
          { label: "Buyer scores", slug: "buyer-scores", href: P("buyer-scores"), desc: "Real-time trade-credit health on every buyer.", icon: TrendingUp },
          { label: "Insurance",    slug: "insurance",    href: P("insurance"),    desc: "Allianz-backed cover on every invoice.",       icon: Shield },
        ],
      },
    ],
  },
  {
    id: "solutions",
    label: "Solutions",
    mega: true,
    columns: [
      {
        heading: "By business type",
        items: [
          { label: "Brands",       slug: "brands",       href: S("brands"),       desc: "DTC brands moving into wholesale.",       icon: Sparkles },
          { label: "Distributors", slug: "distributors", href: S("distributors"), desc: "Multi-line distributors & wholesalers.", icon: Building2 },
          { label: "3PLs",         slug: "3pls",         href: S("3pls"),         desc: "Fulfillment partners embedding Verqo.",  icon: Warehouse },
        ],
      },
      {
        heading: "By role · in wholesale",
        items: [
          { label: "Founders & CEOs",     slug: "founder-ceo",       href: R("founder-ceo"),       desc: "For founders scaling a wholesale channel from zero.", icon: Briefcase },
          { label: "CFOs & Controllers",  slug: "cfo",               href: R("cfo"),               desc: "For finance leaders at wholesale brands & distributors killing DSO.", icon: LineChart },
          { label: "Head of Wholesale",   slug: "head-of-wholesale", href: R("head-of-wholesale"), desc: "For the person owning the wholesale P&L.", icon: Wallet },
          { label: "Head of Ops",         slug: "head-of-ops",       href: R("head-of-ops"),       desc: "For ops leaders killing invoice, shipping, and claims work.", icon: BoxesIcon },
        ],
      },
      {
        heading: "By industry",
        items: [
          { label: "Beauty",   slug: "beauty",   href: I("beauty"),   desc: "Prestige, indie, and drugstore beauty.",    icon: Sparkles },
          { label: "Apparel",  slug: "apparel",  href: I("apparel"),  desc: "Fashion, accessories, footwear.",           icon: Shirt },
          { label: "Food",     slug: "food",     href: I("food"),     desc: "Specialty food, coffee, beverage.",         icon: UtensilsCrossed },
          { label: "Home",     slug: "home",     href: I("home"),     desc: "Home goods, furniture, decor.",             icon: Home },
          { label: "Health",   slug: "health",   href: I("health"),   desc: "Wellness, supplements, personal care.",     icon: Heart },
          { label: "CPG",      slug: "cpg",      href: I("cpg"),      desc: "Consumer packaged goods at scale.",         icon: ShoppingBasket },
        ],
      },
    ],
  },
  {
    id: "pricing",
    label: "Pricing",
    href: "/pricing",
  },
  {
    id: "company",
    label: "Company",
    mega: true,
    columns: [
      {
        heading: "About Verqo",
        items: [
          { label: "Our story",    slug: "story",         href: C("story"),         desc: "Why we built Verqo — from a warehouse floor." },
          { label: "Why Verqo",    slug: "why",           href: C("why"),           desc: "The wholesale-OS thesis in 90 seconds." },
          { label: "How it works", slug: "how-it-works",  href: C("how-it-works"),  desc: "The six-player ledger, explained." },
          { label: "Team",         slug: "team",          href: C("team"),          desc: "Operators, engineers, wholesalers." },
          { label: "Careers",      slug: "careers",       href: C("careers"),       desc: "We're hiring across every function." },
          { label: "Press",        slug: "press",         href: C("press"),         desc: "News, coverage, and press kit." },
        ],
      },
      {
        heading: "Trust & partners",
        items: [
          { label: "Partners", slug: "partners", href: C("partners"), desc: "Banks, insurers, integrators, resellers." },
          { label: "Security", slug: "security", href: C("security"), desc: "SOC 2, bank-grade encryption, examiner-ready." },
          { label: "Privacy",  slug: "privacy",  href: C("privacy"),  desc: "How we handle your data, plainly stated." },
          { label: "Contact",  slug: "contact",  href: C("contact"),  desc: "Talk to a real wholesaler-turned-founder." },
          { label: "Status",   slug: "status",   href: C("status"),   desc: "Live uptime and incident history." },
        ],
      },
    ],
  },
];
